import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { completeLogs, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { DecomLogModel } from '../../../../../types';
import Typography from '@mui/material/Typography';

interface DecomCompleteDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  models: DecomLogModel[];
}

export default function DecomCompleteDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  models,
}: DecomCompleteDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const selectedModels =
    models.filter((item) =>
      selectedRows.includes(item.date.toString() + item.userId)
    ) || [];

  const { mutate, isPending } = useMutation({
    mutationFn: () => completeLogs(selectedModels),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The selected decom log has been completed.'
            : 'The selected decom logs have been completed.',
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.DecomLogs] });
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue completing the selected decom log. Please refresh and try again.'
            : 'There was an issue completing the selected decom logs. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.decommissioning.complete_decom_logs, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Complete Decommissioning ${
        selectedModels.length === 1 ? 'Log' : 'Logs'
      }`}
      dialogContent={
        <>
          {selectedModels.length > 1 && (
            <Typography variant="subtitle1" gutterBottom>
              Complete the{' '}
              <strong>{selectedModels.length} decommissioning logs</strong>.
            </Typography>
          )}
          {selectedModels.length === 1 && (
            <Typography variant="subtitle1" gutterBottom>
              Complete the decommissioning log for{' '}
              <strong>{selectedModels[0].userEmail}</strong>.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: `Complete ${selectedModels.length === 1 ? 'Log' : 'Logs'}`,
        action: () => mutate(),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
